
import { useState, useRef, useEffect } from "react";
import Navbar from "@/components/layout/Navbar";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, Video, Image as ImageIcon, Download, Play, RefreshCw, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getKieApiKey, getKieApiEndpoint } from "@/lib/kie-service";

interface JobResult {
    status: "idle" | "uploading" | "processing" | "completed" | "error";
    taskId?: string;
    resultUrl?: string;
    message?: string;
    progress?: number;
}

const VideoDance = () => {
    const { t } = useLanguage();
    const { user } = useAuth();
    const { toast } = useToast();

    const [refImage, setRefImage] = useState<File | null>(null);
    const [refVideo, setRefVideo] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [videoPreview, setVideoPreview] = useState<string | null>(null);

    const [prompt, setPrompt] = useState("The cartoon character is dancing.");
    const [orientation, setOrientation] = useState("video");
    const [mode, setMode] = useState("720p");

    const [jobResult, setJobResult] = useState<JobResult>({ status: "idle" });

    const fileInputImage = useRef<HTMLInputElement>(null);
    const fileInputVideo = useRef<HTMLInputElement>(null);

    // Cleanup object URLs to avoid memory leaks
    useEffect(() => {
        return () => {
            if (imagePreview) URL.revokeObjectURL(imagePreview);
            if (videoPreview) URL.revokeObjectURL(videoPreview);
        };
    }, [imagePreview, videoPreview]);

    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate
        if (!file.type.startsWith('image/')) {
            toast({ variant: "destructive", title: "Invalid File", description: "Please upload an image file." });
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast({ variant: "destructive", title: "File Too Large", description: "Max 10MB for images." });
            return;
        }

        setRefImage(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate
        if (!file.type.startsWith('video/')) {
            toast({ variant: "destructive", title: "Invalid File", description: "Please upload a video file." });
            return;
        }
        if (file.size > 100 * 1024 * 1024) {
            toast({ variant: "destructive", title: "File Too Large", description: "Max 100MB for videos." });
            return;
        }

        setRefVideo(file);
        setVideoPreview(URL.createObjectURL(file));
    };

    const uploadFile = async (file: File, folder: string) => {
        if (!user) throw new Error("User not logged in");

        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        // Using a 'temp-videodance' folder - ensure bucket exists or policy allows
        // Using 'generation-inputs' as likely existing bucket
        const filePath = `videodance-temp/${user.id}/${fileName}`;

        // We try 'generation-inputs' first as it's used in Generate.tsx
        const { error: uploadError } = await supabase.storage
            .from('generation-inputs')
            .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
            .from('generation-inputs')
            .getPublicUrl(filePath);

        return publicUrl;
    };

    const handleGenerate = async () => {
        if (!user) {
            toast({ variant: "destructive", title: "Error", description: "Please login first." });
            return;
        }
        if (!refImage || !refVideo) {
            toast({ variant: "destructive", title: "Missing Files", description: "Please upload both an image and a video." });
            return;
        }

        setJobResult({ status: "uploading", message: t("กำลังอัพโหลดไฟล์...", "Uploading files...") });

        try {
            // 1. Upload Files
            const imageUrl = await uploadFile(refImage, "images");
            const videoUrl = await uploadFile(refVideo, "videos");

            setJobResult({ status: "processing", message: t("กำลังส่งงานไปที่ AI...", "Submitting task to AI...") });

            // 2. Call Kie API
            const apiKey = await getKieApiKey();
            // Use proxy or direct depending on environment. Usually direct in client-side if CORS allowed, 
            // but here we likely use the same pattern as RemoveSora or Generate (direct fetch with API key).
            // Verify endpoint from user request: POST https://api.kie.ai/api/v1/jobs/createTask
            const baseUrl = "https://api.kie.ai"; // or getKieApiEndpoint() if implemented

            const payload = {
                model: "kling-2.6/motion-control",
                input: {
                    prompt: prompt || "The cartoon character is dancing.",
                    input_urls: [imageUrl],
                    video_urls: [videoUrl],
                    character_orientation: orientation,
                    mode: mode
                }
            };

            const response = await fetch(`${baseUrl}/api/v1/jobs/createTask`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${apiKey}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.code !== 200) {
                throw new Error(data.msg || "Failed to create task");
            }

            const taskId = data.data.taskId;
            setJobResult({ status: "processing", taskId, message: t("รอคิวประมวลผล (ประมาณ 5-10 นาที)...", "Processing (approx 5-10 mins)...") });

            // Start Polling
            pollStatus(taskId, apiKey, baseUrl);

        } catch (error: any) {
            console.error("Generation error:", error);
            setJobResult({ status: "error", message: error.message || "Something went wrong" });
        }
    };

    const pollStatus = async (taskId: string, apiKey: string, baseUrl: string) => {
        let attempts = 0;
        const maxAttempts = 120; // 20 mins (10s interval)

        const check = async () => {
            if (attempts >= maxAttempts) {
                setJobResult(prev => ({ ...prev, status: "error", message: "Timeout waiting for result." }));
                return;
            }

            try {
                const res = await fetch(`${baseUrl}/api/v1/jobs/recordInfo?taskId=${taskId}`, {
                    headers: { "Authorization": `Bearer ${apiKey}` }
                });
                const data = await res.json();

                if (data.code === 200 && data.data) {
                    const { state, resultJson, failMsg } = data.data;

                    if (state === "success" && resultJson) {
                        const result = JSON.parse(resultJson);
                        // Structure: { resultUrls: [...] }
                        const videoUrl = result.resultUrls?.[0];

                        if (videoUrl) {
                            setJobResult({ status: "completed", taskId, resultUrl: videoUrl });
                            toast({ title: "Success!", description: "Video generated successfully." });
                        } else {
                            throw new Error("No URL in result");
                        }
                    } else if (state === "fail") {
                        setJobResult({ status: "error", taskId, message: failMsg || "Task failed" });
                    } else {
                        // Waiting
                        attempts++;
                        setTimeout(check, 10000);
                    }
                } else {
                    attempts++;
                    setTimeout(check, 10000);
                }
            } catch (e) {
                console.error("Poll error:", e);
                attempts++;
                setTimeout(check, 10000);
            }
        };

        check();
    };

    const handleDownload = async (url: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `videodance_${Date.now()}.mp4`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (e) {
            console.error("Download failed:", e);
            window.open(url, '_blank');
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <Navbar />
            <main className="container mx-auto px-4 pt-24 pb-12">
                <div className="flex flex-col gap-8 max-w-5xl mx-auto">

                    <div className="text-center space-y-2">
                        <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                            Motion Control (Video Dance)
                        </h1>
                        <p className="text-muted-foreground">
                            {t("สร้างวิดีโอเต้นจากภาพและวิดีโอต้นแบบ", "Create dance videos from reference image and video")}
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {/* INPUTS COLUMN */}
                        <div className="space-y-6">
                            <Card variant="glass">
                                <CardHeader>
                                    <CardTitle>{t("1. อัพโหลดไฟล์", "1. Upload Files")}</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">

                                    {/* Image Upload */}
                                    <div className="space-y-2">
                                        <Label>{t("รูปภาพต้นแบบ (Character)", "Reference Image (Character)")}</Label>
                                        <div
                                            className="border-2 border-dashed rounded-lg p-4 hover:bg-muted/50 cursor-pointer transition-colors text-center relative h-48 flex items-center justify-center overflow-hidden"
                                            onClick={() => fileInputImage.current?.click()}
                                        >
                                            {imagePreview ? (
                                                <>
                                                    <img src={imagePreview} alt="Preview" className="w-full h-full object-contain" />
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity text-white font-medium">
                                                        {t("คลิกเพื่อเปลี่ยน", "Click to Change")}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                                                    <ImageIcon className="h-8 w-8" />
                                                    <span>Click to upload image</span>
                                                    <span className="text-xs">Max 10MB, JPG/PNG</span>
                                                </div>
                                            )}
                                        </div>
                                        <Input ref={fileInputImage} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
                                    </div>

                                    {/* Video Upload */}
                                    <div className="space-y-2">
                                        <Label>{t("วิดีโอท่าเต้น (Motion)", "Reference Video (Motion)")}</Label>
                                        <div
                                            className="border-2 border-dashed rounded-lg p-4 hover:bg-muted/50 cursor-pointer transition-colors text-center relative h-48 flex items-center justify-center overflow-hidden"
                                            onClick={() => fileInputVideo.current?.click()}
                                        >
                                            {videoPreview ? (
                                                <>
                                                    <video src={videoPreview} className="w-full h-full object-contain" />
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity text-white font-medium">
                                                        {t("คลิกเพื่อเปลี่ยน", "Click to Change")}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                                                    <Video className="h-8 w-8" />
                                                    <span>Click to upload video</span>
                                                    <span className="text-xs">Max 100MB, MP4</span>
                                                </div>
                                            )}
                                        </div>
                                        <Input ref={fileInputVideo} type="file" accept="video/*" className="hidden" onChange={handleVideoSelect} />
                                    </div>

                                </CardContent>
                            </Card>

                            <Card variant="glass">
                                <CardHeader>
                                    <CardTitle>{t("2. ตั้งค่า", "2. Settings")}</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>Prompt (Optional)</Label>
                                        <Input
                                            value={prompt}
                                            onChange={(e) => setPrompt(e.target.value)}
                                            placeholder="The cartoon character is dancing."
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Orientation</Label>
                                            <Select value={orientation} onValueChange={setOrientation}>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="video">Video (Follow Video)</SelectItem>
                                                    <SelectItem value="image">Image (Follow Image)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <p className="text-xs text-muted-foreground">
                                                {orientation === 'image' ? 'Max 10s' : 'Max 30s'}
                                            </p>
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Quality Mode</Label>
                                            <Select value={mode} onValueChange={setMode}>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="720p">Standard (720p)</SelectItem>
                                                    <SelectItem value="1080p">Pro (1080p)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    <Button
                                        className="w-full h-12 text-lg font-bold shadow-lg shadow-primary/20"
                                        onClick={handleGenerate}
                                        disabled={jobResult.status === 'uploading' || jobResult.status === 'processing'}
                                    >
                                        {jobResult.status === 'uploading' || jobResult.status === 'processing' ? (
                                            <>
                                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <Play className="mr-2 h-5 w-5 fill-current" />
                                                Generate Video
                                            </>
                                        )}
                                    </Button>

                                </CardContent>
                            </Card>
                        </div>

                        {/* PREVIEW/RESULT COLUMN */}
                        <div className="space-y-6">
                            <Card variant="glass" className="h-full flex flex-col">
                                <CardHeader>
                                    <CardTitle>{t("ผลลัพธ์", "Result")}</CardTitle>
                                    <CardDescription>
                                        {jobResult.status === 'idle' && "Ready to generate"}
                                        {jobResult.status === 'processing' && jobResult.message}
                                        {jobResult.status === 'completed' && "Generation successful!"}
                                        {jobResult.status === 'error' && "Generation failed"}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="flex-1 flex flex-col items-center justify-center min-h-[400px] bg-black/20 rounded-lg m-4 border-2 border-dashed border-muted">

                                    {jobResult.status === 'idle' && (
                                        <div className="text-center text-muted-foreground p-8">
                                            <Play className="h-16 w-16 mx-auto mb-4 opacity-20" />
                                            <p>Upload files and click Generate to start</p>
                                        </div>
                                    )}

                                    {(jobResult.status === 'uploading' || jobResult.status === 'processing') && (
                                        <div className="text-center space-y-4 p-8">
                                            <div className="relative h-20 w-20 mx-auto">
                                                <div className="absolute inset-0 rounded-full border-4 border-primary/20"></div>
                                                <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                                            </div>
                                            <p className="text-lg font-medium animate-pulse">{jobResult.message}</p>
                                            <p className="text-sm text-muted-foreground">Please do not close this page</p>
                                        </div>
                                    )}

                                    {jobResult.status === 'completed' && jobResult.resultUrl && (
                                        <div className="w-full space-y-4">
                                            <video
                                                src={jobResult.resultUrl}
                                                controls
                                                autoPlay
                                                loop
                                                className="w-full max-h-[600px] rounded-lg shadow-2xl"
                                            />
                                            <div className="flex gap-4 justify-center">
                                                <Button onClick={() => handleDownload(jobResult.resultUrl!)} variant="default">
                                                    <Download className="mr-2 h-4 w-4" />
                                                    Download
                                                </Button>
                                                <Button onClick={() => setJobResult({ status: 'idle' })} variant="outline">
                                                    Make Another
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {jobResult.status === 'error' && (
                                        <div className="text-center text-red-500 p-8 space-y-4">
                                            <div className="h-16 w-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto">
                                                <X className="h-8 w-8" />
                                            </div>
                                            <div>
                                                <p className="font-bold text-lg">Error</p>
                                                <p>{jobResult.message}</p>
                                            </div>
                                            <Button onClick={() => setJobResult({ status: 'idle' })} variant="outline">
                                                Try Again
                                            </Button>
                                        </div>
                                    )}

                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default VideoDance;
